import { useState } from "react";
import { can } from "../../utils/permissions";
import md5 from "md5";
import { hasValue, parseNullableInteger, sanitizeUserIdentityFields } from "./userFieldUtils";

function CreateUserTab(props) {
    const { socket, onCreated, userId } = props;

    const [nom, setNom] = useState('');
    const [cognom, setCognom] = useState('');
    const [segonCognom, setSegonCognom] = useState('');
    const [mote, setMote] = useState('');
    const [altura, setAltura] = useState('');
    const [altura_mans, setAlturaMans] = useState('');
    const [tecnica, setTecnica] = useState(0);
    const [junta, setJunta] = useState(0);
    const [canalla, setCanalla] = useState(false);
    const [music, setMusic] = useState(false);
    const [lesionat, setLesionat] = useState(false);
    const [casteller, setCasteller] = useState(true);
    const [novell, setNovell] = useState(false);
    const [passwordOption, setPasswordOption] = useState('default');
    const [mail, setMail] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [createdUser, setCreatedUser] = useState(null);
    
    // Password modal state
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [modalPassword, setModalPassword] = useState('');
    const [copied, setCopied] = useState(false);

    const generate_default_password = user => md5(`default.${user.nom}.${user['primer-cognom']}`).slice(0,8);

    const resetForm = () => {
        setNom('');
        setCognom('');
        setSegonCognom('');
        setMote('');
        setAltura('');
        setAlturaMans('');
        setTecnica(0);
        setJunta(0);
        setCanalla(false);
        setMusic(false);
        setLesionat(false);
        setCasteller(true);
        setNovell(false);
        setPasswordOption('default');
        setMail('');
        setCreatedUser(null);
        setShowPasswordModal(false);
        setModalPassword('');
        setCopied(false);
    };

    const handleCopyPassword = () => {
        navigator.clipboard.writeText(modalPassword).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1000);
        });
    };

    const createUser = () => {
        const identityFields = sanitizeUserIdentityFields({
            'nom': nom,
            'primer-cognom': cognom,
            'segon-cognom': segonCognom,
            'mote': mote,
            'mail': mail,
        });

        if (identityFields.nom === '' || identityFields['primer-cognom'] === '') {
            alert('El nom i el cognom són camps obligatoris.');
            return;
        }

        if (isSubmitting) return;

        const responses = {
            ...identityFields,
            'altura': parseNullableInteger(altura),
            'altura_mans': parseNullableInteger(altura_mans),
            'tecnica': parseInt(tecnica),
            'junta': parseInt(junta),
            'canalla': canalla ? 1 : 0,
            'music': music ? 1 : 0,
            'lesionat': lesionat ? 1 : 0,
            'casteller': casteller ? 1 : 0,
            'novell': novell ? 1 : 0,
            'password_option': passwordOption,
            '_actorId': userId, // Fallback for socket.userId
        };

        setIsSubmitting(true);

        socket.emit('.create_user', responses, (response) => {
            setIsSubmitting(false);

            if (!response || response.success !== true) {
                if (response?.error === 'FORBIDDEN') {
                    alert('No tens permís per crear castellers. Demana a un cap de tècnica o de junta que revisi els teus permisos.');
                } else if (response?.reason === 'duplicate') {
                    const segon = response['segon-cognom'] ? ` ${response['segon-cognom']}` : '';
                    alert(`Ja existeix un casteller anomenat ${response.nom} ${response['primer-cognom']}${segon}.`);
                } else if (response?.reason === 'duplicate_deleted') {
                    const segon = response['segon-cognom'] ? ` ${response['segon-cognom']}` : '';
                    const shouldRestore = window.confirm(`Ja existeix un casteller eliminat anomenat ${response.nom} ${response['primer-cognom']}${segon}.\n\nVols restaurar aquest casteller?`);

                    if (!shouldRestore) {
                        return;
                    }

                    if (!response.duplicateUserId) {
                        alert('No s\'ha pogut identificar el casteller eliminat per restaurar.');
                        return;
                    }

                    setIsSubmitting(true);
                    socket.emit('.restore_user', {
                        id: response.duplicateUserId,
                        _actorId: userId,
                    }, (restoreResponse) => {
                        setIsSubmitting(false);

                        if (!restoreResponse?.success) {
                            alert(restoreResponse?.error || 'No s\'ha pogut restaurar el casteller.');
                            return;
                        }

                        socket.emit('.request_users');
                    });
                } else if (response?.reason === 'validation' || response?.reason === 'permission') {
                    alert(response?.error || 'No s\'ha pogut crear el casteller.');
                } else {
                    alert('No s\'ha pogut crear el casteller. Torna-ho a provar.');
                }
                return;
            }

            socket.emit('.request_users');
            
            // Store created user data for display
            const userData = {
                nom: identityFields.nom,
                'primer-cognom': identityFields['primer-cognom'],
                'segon-cognom': identityFields['segon-cognom'],
                mote: identityFields.mote,
                altura: parseNullableInteger(altura),
                altura_mans: parseNullableInteger(altura_mans),
                tecnica: tecnica,
                junta: junta,
                canalla: canalla,
                music: music,
                lesionat: lesionat,
                casteller: casteller,
                novell: novell,
            };
            
            setCreatedUser(userData);
            
            // Show password modal if using default password and no email provided
            if (passwordOption === 'default' && !identityFields.mail) {
                const generatedPassword = generate_default_password(identityFields);
                setModalPassword(generatedPassword);
                setShowPasswordModal(true);
            }
        });
    };

    const getTecnicaLabel = (value) => {
        if (value === 2) return 'Cap de tècnica';
        if (value === 1) return 'Tècnica';
        return 'No';
    };

    const getJuntaLabel = (value) => {
        if (value === 2) return 'Cap de junta';
        if (value === 1) return 'Junta';
        return 'No';
    };

    const inputStyle = {
        padding: '8px 12px',
        fontSize: '14px',
        border: '1px solid #d0d0d0',
        borderRadius: '4px',
        width: '100%',
        boxSizing: 'border-box',
        backgroundColor: '#fafafa',
    };

    const radioGroupStyle = {
        display: 'flex',
        gap: '16px',
        alignItems: 'center',
        padding: '8px 0',
    };

    const labelStyle = {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        fontSize: '14px',
        cursor: 'pointer',
    };

    const checkboxLabelStyle = {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        fontSize: '14px',
        cursor: 'pointer',
        padding: '4px 0',
    };

    const fieldGroupStyle = {
        marginBottom: '16px',
    };

    const fieldLabelStyle = {
        fontSize: '13px',
        fontWeight: '600',
        color: '#333',
        marginBottom: '6px',
        display: 'block',
    };

    const sectionStyle = {
        padding: '16px 0',
        borderBottom: '1px solid #e8e8e8',
    };

    const buttonStyle = {
        padding: '10px 24px',
        fontSize: '15px',
        fontWeight: '500',
        border: '1px solid #2563eb',
        borderRadius: '4px',
        backgroundColor: '#2563eb',
        color: 'white',
        cursor: isSubmitting ? 'not-allowed' : 'pointer',
        opacity: isSubmitting ? 0.7 : 1,
    };

    const cancelButtonStyle = {
        padding: '10px 24px',
        fontSize: '15px',
        fontWeight: '500',
        border: '1px solid #d0d0d0',
        borderRadius: '4px',
        backgroundColor: 'white',
        color: '#333',
        cursor: 'pointer',
        textDecoration: 'none',
    };

    // Password Modal Component
    const PasswordModal = () => (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
        }}>
            <div style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '24px',
                maxWidth: '500px',
                width: '90%',
                boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                    <span style={{ fontSize: '28px' }}>🔑</span>
                    <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>Contrasenya del nou casteller</h3>
                </div>
                
                <p style={{ margin: '0 0 16px', color: '#666', fontSize: '14px' }}>
                    Guarda aquesta contrasenya, és l'única vegada que la veuràs:
                </p>
                
                <div style={{ position: 'relative', marginBottom: '16px' }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '8px',
                        border: '1px solid #e0e0e0',
                        overflow: 'hidden',
                    }}>
                        <div style={{
                            flex: 1,
                            padding: '14px 16px',
                            fontFamily: 'monospace',
                            fontSize: '18px',
                            fontWeight: '600',
                            letterSpacing: '1px',
                            color: '#1a1a1a',
                        }}>
                            {modalPassword}
                        </div>
                        <button 
                            onClick={handleCopyPassword} 
                            disabled={copied} 
                            style={{
                                width: '100px',
                                padding: '14px',
                                backgroundColor: copied ? '#4CAF50' : '#3498db',
                                color: 'white',
                                border: 'none',
                                cursor: copied ? 'default' : 'pointer',
                                fontWeight: '500',
                                fontSize: '15px',
                                transition: 'background-color 0.2s ease',
                            }}
                        >
                            {copied ? 'Copiat!' : 'Copiar'}
                        </button>
                    </div>
                </div>
                
                <div style={{ margin: '16px 0', fontSize: '14px', color: '#666', lineHeight: '1.5' }}>
                    <p style={{ margin: '0 0 8px' }}>
                        <strong>Important:</strong> Quan tanquis aquesta finestra, ja no podràs tornar a veure la contrasenya.
                    </p>
                    <p style={{ margin: 0 }}>
                        Aquesta és la contrasenya inicial que el casteller haurà d'utilitzar per accedir a l'aplicació.
                    </p>
                </div>
                
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
                    <button 
                        onClick={() => setShowPasswordModal(false)} 
                        style={{
                            padding: '10px 20px',
                            fontSize: '14px',
                            fontWeight: '500',
                            border: 'none',
                            borderRadius: '6px',
                            backgroundColor: '#1a1a1a',
                            color: 'white',
                            cursor: 'pointer',
                        }}
                    >
                        Entès, tancar
                    </button>
                </div>
            </div>
        </div>
    );

    // Show success message with created user data
    if (createdUser) {
        const fullName = [
            createdUser.nom,
            createdUser['primer-cognom'],
            createdUser['segon-cognom']
        ].filter(Boolean).join(' ');

        const tags = [];
        if (createdUser.casteller) tags.push('Casteller');
        if (createdUser.novell) tags.push('Novell');
        if (createdUser.canalla) tags.push('Canalla');
        if (createdUser.music) tags.push('Músic');
        if (createdUser.lesionat) tags.push('Lesionat');

        return (
            <>
                {showPasswordModal ? <PasswordModal /> : null}
                <div style={{ 
                    maxWidth: '600px', 
                    margin: '0 auto', 
                    backgroundColor: 'white', 
                    borderRadius: '8px', 
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    padding: '24px',
                }}>
                    <div style={{
                        backgroundColor: '#d4edda',
                        border: '1px solid #c3e6cb',
                        borderRadius: '8px',
                        padding: '16px 20px',
                        marginBottom: '24px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                    }}>
                        <span style={{ fontSize: '24px' }}>✓</span>
                        <div>
                            <div style={{ fontWeight: '600', color: '#155724', fontSize: '16px' }}>
                                Casteller creat correctament!
                            </div>
                            <div style={{ color: '#155724', fontSize: '14px', marginTop: '2px' }}>
                                S'ha afegit {createdUser.mote ? `"${createdUser.mote}"` : fullName} a la llista de castellers.
                            </div>
                        </div>
                    </div>

                    <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600', color: '#1a1a1a' }}>
                        Dades del casteller creat
                    </h3>

                    <div style={{ 
                        backgroundColor: '#f8f9fa', 
                        borderRadius: '8px', 
                        padding: '20px',
                        marginBottom: '24px',
                    }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <tbody>
                                <tr>
                                    <td style={{ padding: '8px 12px 8px 0', fontWeight: '600', color: '#666', width: '140px', verticalAlign: 'top' }}>Nom complet:</td>
                                    <td style={{ padding: '8px 0', color: '#1a1a1a' }}>{fullName}</td>
                                </tr>
                                {createdUser.mote ? (
                                    <tr>
                                        <td style={{ padding: '8px 12px 8px 0', fontWeight: '600', color: '#666', verticalAlign: 'top' }}>Sobrenom:</td>
                                        <td style={{ padding: '8px 0', color: '#1a1a1a' }}>{createdUser.mote}</td>
                                    </tr>
                                ) : null}
                                <tr>
                                    <td style={{ padding: '8px 12px 8px 0', fontWeight: '600', color: '#666', verticalAlign: 'top' }}>Altura:</td>
                                    <td style={{ padding: '8px 0', color: '#1a1a1a' }}>
                                        {hasValue(createdUser.altura) ? `${createdUser.altura} cm` : '—'}
                                    </td>
                                </tr>
                                <tr>
                                    <td style={{ padding: '8px 12px 8px 0', fontWeight: '600', color: '#666', verticalAlign: 'top' }}>Altura mans:</td>
                                    <td style={{ padding: '8px 0', color: '#1a1a1a' }}>
                                        {hasValue(createdUser.altura_mans) ? `${createdUser.altura_mans} cm` : '—'}
                                    </td>
                                </tr>
                                <tr>
                                    <td style={{ padding: '8px 12px 8px 0', fontWeight: '600', color: '#666', verticalAlign: 'top' }}>Tècnica:</td>
                                    <td style={{ padding: '8px 0', color: '#1a1a1a' }}>{getTecnicaLabel(createdUser.tecnica)}</td>
                                </tr>
                                <tr>
                                    <td style={{ padding: '8px 12px 8px 0', fontWeight: '600', color: '#666', verticalAlign: 'top' }}>Junta:</td>
                                    <td style={{ padding: '8px 0', color: '#1a1a1a' }}>{getJuntaLabel(createdUser.junta)}</td>
                                </tr>
                                <tr>
                                    <td style={{ padding: '8px 12px 8px 0', fontWeight: '600', color: '#666', verticalAlign: 'top' }}>Etiquetes:</td>
                                    <td style={{ padding: '8px 0' }}>
                                        {tags.length > 0 ? (
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                                {tags.map(tag => (
                                                    <span key={tag} style={{
                                                        backgroundColor: '#e9ecef',
                                                        padding: '4px 10px',
                                                        borderRadius: '12px',
                                                        fontSize: '13px',
                                                        color: '#495057',
                                                    }}>
                                                        {tag}
                                                    </span>
                                                ))}
                                            </div>
                                        ) : (
                                            <span style={{ color: '#999' }}>—</span>
                                        )}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                        <button 
                            style={cancelButtonStyle} 
                            onClick={() => onCreated && onCreated()}
                        >
                            Anar a Usuaris
                        </button>
                        <button 
                            style={buttonStyle} 
                            onClick={resetForm}
                        >
                            + Crear un altre
                        </button>
                    </div>
                </div>
            </>
        );
    }

    return (
        <div style={{ 
            maxWidth: '600px', 
            margin: '0 auto', 
            backgroundColor: 'white', 
            borderRadius: '8px', 
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            padding: '24px',
        }}>
            <h2 style={{ margin: '0 0 20px 0', fontSize: '22px', fontWeight: '600', color: '#1a1a1a' }}>Crear casteller</h2>
            
            {/* Informació personal */}
            <div style={sectionStyle}>
                <div style={fieldGroupStyle}>
                    <label style={fieldLabelStyle}>Nom *</label>
                    <input type="text" style={inputStyle} value={nom} onChange={e => setNom(e.target.value)} placeholder="Introdueix el nom" />
                </div>
                <div style={fieldGroupStyle}>
                    <label style={fieldLabelStyle}>1r cognom *</label>
                    <input type="text" style={inputStyle} value={cognom} onChange={e => setCognom(e.target.value)} placeholder="Introdueix el primer cognom" />
                </div>
                <div style={fieldGroupStyle}>
                    <label style={fieldLabelStyle}>2n cognom</label>
                    <input type="text" style={inputStyle} value={segonCognom} onChange={e => setSegonCognom(e.target.value)} placeholder="Introdueix el segon cognom (opcional)" />
                </div>
                <div style={fieldGroupStyle}>
                    <label style={fieldLabelStyle}>Sobrenom</label>
                    <input type="text" style={inputStyle} value={mote} onChange={e => setMote(e.target.value)} placeholder="Introdueix el sobrenom" />
                </div>
            </div>

            {/* Característiques físiques */}
            <div style={sectionStyle}>
                <div style={fieldGroupStyle}>
                    <label style={fieldLabelStyle}>Altura (cm)</label>
                    <input type="number" style={inputStyle} value={altura} onChange={e => setAltura(e.target.value)} onWheel={(e) => e.target.blur()} placeholder="Introdueix l'altura en cm" />
                </div>
                <div style={fieldGroupStyle}>
                    <label style={fieldLabelStyle}>Altura mans (cm)</label>
                    <input type="number" style={inputStyle} value={altura_mans} onChange={e => setAlturaMans(e.target.value)} onWheel={(e) => e.target.blur()} placeholder="Introdueix l'altura de mans en cm" />
                </div>
            </div>

            {/* Rols */}
            <div style={sectionStyle}>
                <div style={fieldGroupStyle}>
                    <label style={fieldLabelStyle}>És tècnica:</label>
                    <div style={radioGroupStyle}>
                        { can(props.userInfo, 'ASSIGN_HEAD_TECNICA') ? 
                            <label style={labelStyle}>
                                <input onChange={() => setTecnica(2)} name="tecnica" type="radio" checked={parseInt(tecnica) === 2} />
                                <span>Cap de tècnica</span>
                            </label> : null 
                        }
                        { can(props.userInfo, 'ASSIGN_TECNICA') ?
                            <label style={labelStyle}>
                                <input onChange={() => setTecnica(1)} name="tecnica" type="radio" checked={parseInt(tecnica) === 1} />
                                <span>Tècnica</span>
                            </label> : null
                        }
                        <label style={labelStyle}>
                            <input onChange={() => setTecnica(0)} name="tecnica" type="radio" checked={parseInt(tecnica) === 0} />
                            <span>No tècnica</span>
                        </label>
                    </div>
                </div>
                
                <div style={fieldGroupStyle}>
                    <label style={fieldLabelStyle}>És junta:</label>
                    <div style={radioGroupStyle}>
                        { can(props.userInfo, 'ASSIGN_HEAD_JUNTA') ? 
                            <label style={labelStyle}>
                                <input onChange={() => setJunta(2)} name="junta" type="radio" checked={parseInt(junta) === 2} />
                                <span>Cap de junta</span>
                            </label> : null 
                        }
                        { can(props.userInfo, 'ASSIGN_JUNTA') ?
                            <label style={labelStyle}>
                                <input onChange={() => setJunta(1)} name="junta" type="radio" checked={parseInt(junta) === 1} />
                                <span>Junta</span>
                            </label> : null
                        }
                        <label style={labelStyle}>
                            <input onChange={() => setJunta(0)} name="junta" type="radio" checked={parseInt(junta) === 0} />
                            <span>No junta</span>
                        </label>
                    </div>
                </div>
            </div>

            {/* Estats */}
            <div style={sectionStyle}>
                <label style={{ ...fieldLabelStyle, marginBottom: '12px' }}>Estat:</label>
                <div>
                    <label style={checkboxLabelStyle}>
                        <input type="checkbox" checked={casteller} onChange={e => setCasteller(e.target.checked)} />
                        <span>És casteller</span>
                    </label>
                    <label style={checkboxLabelStyle}>
                        <input type="checkbox" checked={novell} onChange={e => setNovell(e.target.checked)} />
                        <span>És novell</span>
                    </label>
                    <label style={checkboxLabelStyle}>
                        <input type="checkbox" checked={canalla} onChange={e => setCanalla(e.target.checked)} />
                        <span>És canalla</span>
                    </label>
                    <label style={checkboxLabelStyle}>
                        <input type="checkbox" checked={music} onChange={e => setMusic(e.target.checked)} />
                        <span>És músic</span>
                    </label>
                    <label style={checkboxLabelStyle}>
                        <input type="checkbox" checked={lesionat} onChange={e => setLesionat(e.target.checked)} />
                        <span>Està lesionat</span>
                    </label>
                </div>
            </div>

            {/* Contrasenya */}
            <div style={{ ...sectionStyle, borderBottom: 'none' }}>
                <div style={fieldGroupStyle}>
                    <label style={fieldLabelStyle}>Contrasenya:</label>
                    <div style={radioGroupStyle}>
                        <label style={labelStyle}>
                            <input onChange={() => setPasswordOption('default')} name="password" type="radio" checked={passwordOption === 'default'} />
                            <span>Contrasenya aleatòria</span>
                        </label>
                        <label style={labelStyle}>
                            <input onChange={() => setPasswordOption('empty')} name="password" type="radio" checked={passwordOption === 'empty'} />
                            <span>Sense contrasenya</span>
                        </label>
                    </div>
                </div>
                
                {passwordOption === 'default' ? (
                    <div style={{ ...fieldGroupStyle, marginTop: '12px' }}>
                        <label style={fieldLabelStyle}>Enviar contrasenya per mail (opcional):</label>
                        <input 
                            type="email" 
                            style={inputStyle} 
                            value={mail} 
                            onChange={e => setMail(e.target.value)} 
                            placeholder="mail-per@enviar-la-contrasenya.com" 
                        />
                        <p style={{ margin: '8px 0 0', fontSize: '12px', color: '#666' }}>
                            Si no proporciones un mail, la contrasenya es mostrarà un cop creat el casteller.
                        </p>
                    </div>
                ) : null}
            </div>

            {/* Botons */}
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px', paddingTop: '16px', borderTop: '1px solid #e8e8e8' }}>
                <button style={cancelButtonStyle} onClick={resetForm}>Netejar</button>
                <button style={buttonStyle} onClick={() => createUser()} disabled={isSubmitting}>
                    {isSubmitting ? 'Creant…' : 'Crear'}
                </button>
            </div>
        </div>
    );
}

export default CreateUserTab;
